import { useEffect, useState, useRef } from "react";
import companyApi from "../api/companyApi";
import favoritesApi from "../api/favoritesApi";
import useAuth from "../hooks/useAuth";

import {
  Container,
  Grid,
  CardContent,
  Typography,
  Skeleton,
  TextField,
  MenuItem,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Stack,
  Button,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { ViewModule, TableRows, Star, StarBorder } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { COMMODITY_META, CommodityDots, getCommodityMeta } from '../components/companies/functions';
import CompanyQuickViewDrawer from "../components/companies/CompanyQuickViewDrawer";
import { trackDirectorySearch, trackResultClicked } from "../analytics/ga";
import {
  COUNTRY_OPTIONS,
} from "../constants/companyFields";


const PAGE_SIZE = 50;
const LISTING_TIER_STYLES = {
  gold: {
    background: "linear-gradient(135deg, #fff6d1 0%, #ffe39b 100%)",
    border: "1px solid",
    borderColor: "#f2c874",
  },
  silver: {
    background: "linear-gradient(135deg, #f3f6fb 0%, #e0e7f2 100%)",
    border: "1px solid",
    borderColor: "#c4cfdd",
  },
  copper: {
    background: "linear-gradient(135deg, #fbe6d3 0%, #f1c79e 100%)",
    border: "1px solid",
    borderColor: "#d8a679",
  },
};

export default function CompaniesDirectory() {
  const { user } = useAuth(); // access auth state
  const [companies, setCompanies] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    country: "",
    lifecycle: "",
    commodities: [],
    claimed: "",
    sort: "name_asc",
    view: "grid",
  });
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [pagination, setPagination] = useState({
    limit: PAGE_SIZE,
    offset: 0,
    hasMore: true,
    isLoading: false,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const debounceRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const theme = useTheme();
  const isLoggedIn = Boolean(user && (user.id || user.email || user.username));
  const loading = pagination.isLoading;
  const isInitialFiltersSearchRef = useRef(true);
  const isInitialTextSearchRef = useRef(true);

  useEffect(() => {
    setFiltersOpen(!isMobile);
  }, [isMobile]);

  const buildParams = () => {
    const params = {
      limit: pagination.limit,
      offset: pagination.offset,
      sort: filters.sort,
    };
    if (filters.search) params.search = filters.search;
    if (filters.country) params.country = filters.country;
    if (filters.lifecycle) params.lifecycle = filters.lifecycle;
    if (filters.commodities && filters.commodities.length > 0) {
      params.commodities = filters.commodities.join(",");
    }
    if (filters.claimed) params.claimed = filters.claimed;
    return params;
  };

  const countAppliedFilters = () => {
    let count = 0;
    if (filters.country) count += 1;
    if (filters.lifecycle) count += 1;
    if (filters.claimed) count += 1;
    if (filters.commodities && filters.commodities.length > 0) {
      count += filters.commodities.length;
    }
    return count;
  };

  const fireDirectorySearch = (query) => {
    const hasQueryParams =
      Boolean(query ?? filters.search) ||
      countAppliedFilters() > 0 ||
      filters.sort !== "name_asc";
    if (!hasQueryParams) return;
    trackDirectorySearch({
      query: query ?? filters.search,
      filters_count: countAppliedFilters(),
      sort: filters.sort,
      country: filters.country || undefined,
      lifecycle: filters.lifecycle || undefined,
      claimed: filters.claimed || undefined,
      commodities: filters.commodities,
      view: filters.view,
    });
  };

  const loadCompanies = async (reset = false) => {
    const params = buildParams();
    if (reset) {
      params.offset = 0;
      setPagination((prev) => ({ ...prev, offset: 0, isLoading: true }));
    } else {
      setLoadingMore(true);
      setPagination((prev) => ({ ...prev, isLoading: true }));
    }
    try {
      const data = await companyApi.getCompanies(params);
      const results = Array.isArray(data)
        ? data
        : data?.results ?? (Array.isArray(data?.data) ? data.data : []);
      setCompanies((prev) =>
        reset ? results : [...prev, ...results]
      );
      setPagination((prev) => ({
        ...prev,
        isLoading: false,
        hasMore: Boolean(data?.next) || (data?.next !== null && results.length > 0),
      }));
    } catch {
      setPagination((prev) => ({ ...prev, isLoading: false, hasMore: false }));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // Reset to first page whenever filters that affect the query change
    setPagination((prev) => ({ ...prev, offset: 0 }));
    if (!isInitialFiltersSearchRef.current) {
      fireDirectorySearch();
    }
    loadCompanies(true);
    isInitialFiltersSearchRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.sort, filters.country, filters.lifecycle, filters.commodities, filters.claimed]);

  // search debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const shouldTrack = !isInitialTextSearchRef.current;
    debounceRef.current = setTimeout(() => {
      setPagination((prev) => ({ ...prev, offset: 0 }));
      if (shouldTrack) {
        fireDirectorySearch(filters.search);
      }
      loadCompanies(true);
    }, 400);
    isInitialTextSearchRef.current = false;
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  useEffect(() => {
    if (pagination.offset === 0) return;
    loadCompanies(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.offset]);

  // only fetch favorites if logged in
    useEffect(() => {
      if (isLoggedIn) {
        favoritesApi
          .getFavorites()
          .then((data) => setFavorites(Array.isArray(data) ? data : []))
          .catch(() => {});
      } else {
        setFavorites([]);
      }
    }, [isLoggedIn, user]);

  const isFavorite = (companyId) => favorites.some((fav) => fav.company.id === companyId);

  const handleToggleFavorite = async (company) => {
    const existing = favorites.find((f) => f.company.id === company.id);
    if (existing) {
      await favoritesApi.removeFavorite(existing.id);
      setFavorites((prev) => prev.filter((f) => f.id !== existing.id));
      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, is_favorite: false } : c))
      );
      setSelectedCompany((prev) => (prev?.id === company.id ? { ...prev, is_favorite: false } : prev));
    } else {
      const newFav = await favoritesApi.addFavorite(company.id);
      setFavorites((prev) => [...prev, newFav]);
      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, is_favorite: true } : c))
      );
      setSelectedCompany((prev) => (prev?.id === company.id ? { ...prev, is_favorite: true } : prev));
    }
  };

  const handleCardClick = (company, position) => {
    trackResultClicked({
      result_type: "company",
      result_id: company?.id,
      result_slug: company?.slug,
      position: Number.isFinite(position) ? position + 1 : undefined,
      query: filters.search,
    });
    setSelectedCompany(company);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedCompany(null);
  };

  const filteredCompanies = companies; // server-side filtering

  return (
    <>
    <Container sx={{ mt: 6, mb: 8 }}>
      {/* top controls */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h4" fontWeight={700}>
            Mining Companies Directory
          </Typography>
          <IconButton
            aria-label="toggle filters"
            onClick={() => setFiltersOpen((prev) => !prev)}
            sx={{
              display: { xs: "inline-flex", sm: "none" },
              border: "1px solid",
              borderColor: "divider",
              width: 40,
              height: 40,
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: (filtersOpen || !isMobile) ? "grid" : "none",
            gap: 1.5,
            alignItems: "center",
            gridTemplateColumns: {
              xs: "repeat(auto-fit, minmax(180px, 1fr))",
              sm: "repeat(4, minmax(0, 1fr)) minmax(0, max-content)"
            },
          }}
        >
          <TextField
            size="small"
            label="Search by name"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />

          <TextField
            select
            size="small"
            label="Lifecycle"
            value={filters.lifecycle}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, lifecycle: e.target.value }))
            }
            SelectProps={{
              renderValue: (selected) => {
                if (!selected) return "All";
                const match = [
                  { value: "exploration", label: "Exploration" },
                  { value: "development", label: "Development" },
                  { value: "production", label: "Production" },
                  { value: "closure", label: "Closure" },
                ].find((option) => option.value === selected);
                return match ? match.label : selected;
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {[
              { value: "exploration", label: "Exploration" },
              { value: "development", label: "Development" },
              { value: "production", label: "Production" },
              { value: "closure", label: "Closure" },
            ].map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}
              >
                <span>{option.label}</span>
                {filters.lifecycle === option.value && (
                  <IconButton
                    size="small"
                    aria-label={`clear ${option.label.toLowerCase()} lifecycle filter`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setFilters((prev) => ({ ...prev, lifecycle: "" }));
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </MenuItem>
            ))}
          </TextField>

          <FormControl size="small" fullWidth>
            <InputLabel id="commodities-label">Commodities</InputLabel>
            <Select
              labelId="commodities-label"
              multiple
              value={filters.commodities}
              input={<OutlinedInput label="Commodities" />}
              onChange={(e) => {
                const val = e.target.value;
                const normalized = Array.isArray(val)
                  ? val.map((v) => v.toString().trim().toLowerCase()).filter(Boolean)
                  : [];
                setFilters((prev) => ({ ...prev, commodities: normalized }));
              }}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((val) => {
                    const meta = getCommodityMeta(val);
                    return (
                      <Chip
                        key={val}
                        size="small"
                        label={meta?.symbol || val}
                        sx={{
                          backgroundColor: meta?.color || "action.hover",
                          color: "#0f172a",
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {Object.keys(COMMODITY_META).map((key) => {
                const meta = COMMODITY_META[key];
                const isSelected = filters.commodities.includes(key);
                return (
                  <MenuItem
                    key={key}
                    value={key}
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}
                  >
                    <span>
                      {meta.symbol} — {meta.label}
                    </span>
                    {isSelected && (
                      <IconButton
                        size="small"
                        aria-label={`remove ${meta.label}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setFilters((prev) => ({
                            ...prev,
                            commodities: prev.commodities.filter((c) => c !== key),
                          }));
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </MenuItem>
                );
              })}
              {(() => {
                const isSelected = filters.commodities.includes("rare_earths");
                return (
                  <MenuItem
                    value="rare_earths"
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}
                  >
                    <span>RE — Rare Earths</span>
                    {isSelected && (
                      <IconButton
                        size="small"
                        aria-label="remove rare earths"
                        onClick={(event) => {
                          event.stopPropagation();
                          setFilters((prev) => ({
                            ...prev,
                            commodities: prev.commodities.filter((c) => c !== "rare_earths"),
                          }));
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </MenuItem>
                );
              })()}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel id="country-label">HQ Country</InputLabel>
            <Select
              labelId="country-label"
              label="Filter by Country"
              value={filters.country}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, country: e.target.value }))
              }
              input={<OutlinedInput label="Filter by Country" />}
              renderValue={(selected) => (selected ? selected : "All")}
            >
              <MenuItem value="">All</MenuItem>
              {COUNTRY_OPTIONS.map((c) => (
                <MenuItem
                  key={c}
                  value={c}
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}
                >
                  <span>{c}</span>
                  {filters.country === c && (
                    <IconButton
                      size="small"
                      aria-label={`clear ${c} HQ country filter`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setFilters((prev) => ({ ...prev, country: "" }));
                      }}
                      onMouseDown={(event) => event.stopPropagation()}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filters.claimed === "true"}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      claimed: e.target.checked ? "true" : "",
                    }))
                  }
                />
              }
              label="Claimed"
              sx={{ ml: 1 }}
            />
            <Tooltip title={`Sort ${filters.sort === "name_desc" ? "A-Z" : "Z-A"}`}>
              <IconButton
                size="small"
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    sort: prev.sort === "name_desc" ? "name_asc" : "name_desc",
                  }))
                }
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                }}
              >
                <SortByAlphaIcon
                  fontSize="small"
                  sx={{ transform: filters.sort === "name_desc" ? "scaleX(-1)" : "none" }}
                />
              </IconButton>
            </Tooltip>
            <Box sx={{ display: { xs: "none", sm: "flex" } }}>
              <ToggleButtonGroup
                size="small"
                value={filters.view}
                exclusive
                onChange={(e, v) => v && setFilters((prev) => ({ ...prev, view: v }))}
              >
                <ToggleButton value="grid">
                  <ViewModule />
                </ToggleButton>
                <ToggleButton value="table">
                  <TableRows />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Loading State */}
      {loading && companies.length === 0 && (
      <Grid container spacing={3} sx={{ width: "100%", mt: 1 }}>
          {[...Array(9)].map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Box sx={{ ...theme.mixins.frostedBox, p: 3 }}>
                <Skeleton width="60%" />
                <Skeleton width="40%" />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Grid View */}
      {(filters.view === "grid" || isMobile) && companies && (
        <Grid container spacing={{ xs: 2.4, md: 2.4 }} sx={{ width: "100%", mt: { xs: 2, md: 1.5 } }}>
          {filteredCompanies.map((company, index) => {
            const profile = company.profile || {};
            const tierKey = `${company.directory_tier || ""}`.toLowerCase();
            const tierStyle = LISTING_TIER_STYLES[tierKey] || {};
            return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={company.id}>
              <Box
                role="button"
                onClick={() => handleCardClick(company, index)}
                sx={{
                  ...theme.mixins.frostedBox,
                  ...tierStyle,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  textDecoration: "none",
                  transition: "all 0.25s ease",
                  cursor: "pointer",
                  p: { xs: 2.25, sm: 2.4, md: 2.4 },
                  "& .secondary-text": {
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                  },
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.16)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    px: { xs: 1, sm: 0 },
                    pt: 1,
                    pb: { xs: 1, sm: 1 },
                    "&:last-child": { pb: { xs: 1, sm: 1 } },
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{
                          lineHeight: 1.1,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          mb: 0,
                        }}
                      >
                        {company.name}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexShrink: 0, alignItems: "center", gap: 1 }}>
                      {company.is_claimed && (
                        <Tooltip title="Claimed" placement="top">
                          <CheckCircleIcon color="primary" fontSize="small" sx={{ mr: 0.25 }} />
                        </Tooltip>
                      )}
                      {isLoggedIn ? (
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleFavorite(company);
                          }}
                          size="small"
                          sx={{
                            p: 0,                    
                            alignSelf: "flex-start", 
                            color: isFavorite(company.id)
                              ? "warning.main"
                              : "text.secondary",
                          }}
                        >
                          {isFavorite(company.id) ? (
                            <Star fontSize="small" />
                          ) : (
                            <StarBorder fontSize="small" />
                          )}
                        </IconButton>
                      ) : (
                        <Tooltip title="Log in to favorite companies">
                          <Box
                            component="span"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ alignSelf: "flex-start", lineHeight: 0 }}
                          >
                            <IconButton
                              disabled
                              size="small"
                              sx={{ p: 0, pointerEvents: "none" }}
                            >
                              <StarBorder fontSize="small" />
                            </IconButton>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="secondary-text"
                    sx={{ mt: -0.5 }}
                  >
                  {isMobile
                    ? profile.hq_country
                    : `${profile.hq_city ? `${profile.hq_city}, ` : ""}${profile.hq_country}`}
                  </Typography>
                  <Box sx={{ mt: "auto" }}>
                    {isMobile ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(profile.commodities || []).slice(0, 4).map((c) => {
                          const meta = getCommodityMeta(c);
                          return (
                            <Box
                              key={c}
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 10,
                                backgroundColor: meta?.color || "action.hover",
                                fontSize: "0.75rem",
                                color: "#0f172a",
                              }}
                            >
                              {meta?.symbol || c}
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <CommodityDots commodities={profile.commodities} />
                    )}
                  </Box>
                </CardContent>
              </Box>
            </Grid>
          )})}

          {filteredCompanies.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography textAlign="center" sx={{ mt: 6 }}>
                No companies found.
              </Typography>
            </Grid>
          )}

          {pagination.hasMore && (
            <Grid size={{ xs: 12 }} sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    offset: prev.offset + prev.limit,
                  }))
                }
                disabled={loadingMore || pagination.isLoading}
              >
                {loadingMore ? <CircularProgress size={20} /> : "Load more companies"}
              </Button>
            </Grid>
          )}
        </Grid>
      )}

      {/* Table View */}
      {filters.view === "table" && !isMobile && companies && (
        <Box
          sx={{
            ...theme.mixins.frostedBox,
            mt: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Company </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>HQ Country</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>HQ City</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Commodities</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCompanies.map((company, index) => {
                  const profile = company.profile || {};
                  const tierKey = `${company.directory_tier || ""}`.toLowerCase();
                  const tierStyle = LISTING_TIER_STYLES[tierKey] || {};
                  return (
                  <TableRow
                    key={company.id}
                    hover
                    onClick={() => handleCardClick(company, index)}
                    sx={{
                      ...tierStyle,
                      cursor: "pointer",
                      transition: "background 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.45)",
                      },
                    }}
                  >
                  <TableCell>{company.name}</TableCell>
                  <TableCell>{profile.hq_country}</TableCell>
                  <TableCell>{profile.hq_city || "-"}</TableCell>
                  <TableCell>
                    <CommodityDots commodities={profile.commodities} />
                  </TableCell>
                  <TableCell>
                  <Box sx={{ display:"flex", alignItems:"center", justifyContent: "flex-end", gap: 1}} >
                    {company.is_claimed && (
                      <Tooltip title="Claimed" placement="top">
                        <CheckCircleIcon color="primary" fontSize="small" />
                      </Tooltip>
                    )}
                    {isLoggedIn && (
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleFavorite(company);
                        }}
                        sx={{
                          color: isFavorite(company.id) ? "warning.main" : "text.secondary",
                        }}
                      >
                        {isFavorite(company.id) ? <Star /> : <StarBorder />}
                      </IconButton>
                    )}
                    {!isLoggedIn && (
                      <Tooltip title="Log in to favorite companies">
                        <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ lineHeight: 0 }}>
                          <IconButton disabled sx={{ pointerEvents: "none" }}>
                            <StarBorder />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    )}
                     </Box>
                  </TableCell>
                </TableRow>
              )})}

                {filteredCompanies.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No companies found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          {pagination.hasMore && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Button
                variant="outlined"
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    offset: prev.offset + prev.limit,
                  }))
                }
                disabled={loadingMore || pagination.isLoading}
              >
                {loadingMore ? <CircularProgress size={20} /> : "Load more companies"}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Container>

    <CompanyQuickViewDrawer
      open={drawerOpen}
      onClose={closeDrawer}
      company={selectedCompany}
      isLoggedIn={isLoggedIn}
      favorite={selectedCompany ? isFavorite(selectedCompany.id) : false}
      onToggleFavorite={handleToggleFavorite}
      viewProfileHref={selectedCompany?.slug ? `/companies/${selectedCompany.slug}` : ""}
      viewProfileTarget="_blank"
    />
    </>
  );
}
